/**
 * Ticket Template System - Key-Specific Overrides
 *
 * Overrides for specific ticket_type_key values.
 * These are merged on top of the base group template.
 */

import { TicketContext, TicketTemplateOverride, DEFAULT_FIELD } from './types'
import { SAMPLE_CONTENT } from './sample-content'

/**
 * Condition: High priority incident
 */
const isHighPriorityIncident = (ctx: TicketContext) => ctx.priority === 'high'

/**
 * Key-specific template overrides
 *
 * These overrides are merged on top of the base group template.
 * Only specify fields that differ from the group template.
 */
export const KEY_OVERRIDES: Record<string, TicketTemplateOverride> = {
    /**
     * Error Service Incident
     * - Shows marjorable (Major Incident) checkbox when priority is high
     * - Shows affected field when priority is high
     */
    error_service_incident: {
        fields: {
            marjorable: {
                ...DEFAULT_FIELD,
                visible: true,
                condition: isHighPriorityIncident,
            },
            affected: {
                ...DEFAULT_FIELD,
                visible: true,
                condition: isHighPriorityIncident,
            },
        },
        sampleContent: SAMPLE_CONTENT['key:error_service_incident'],
    },

    /**
     * Service Creating
     * - Only shows start date (editable)
     * - Does not show portal name preview
     */
    service_creating: {
        fields: {
            serviceDates: {
                ...DEFAULT_FIELD,
                visible: true,
                variant: 'creating',
            },
        },
        sampleContent: SAMPLE_CONTENT['key:service_creating'],
    },

    /**
     * Service Creating POC
     * - Shows both dates (both editable)
     * - Filters services by is_poc=true
     */
    service_creating_poc: {
        fields: {
            serviceDates: {
                ...DEFAULT_FIELD,
                visible: true,
                variant: 'poc',
            },
        },
        sampleContent: SAMPLE_CONTENT['key:service_creating_poc'],
        serviceFilter: {
            is_poc: true,
        },
    },

    /**
     * Service Cancelled
     * - Shows both dates
     * - Start date disabled, end date editable
     */
    service_cancelled: {
        fields: {
            serviceDates: {
                ...DEFAULT_FIELD,
                visible: true,
                variant: 'cancelled',
            },
        },
        sampleContent: SAMPLE_CONTENT['key:service_cancelled'],
    },

    /**
     * Service Suspend
     * - Shows start (disabled), end date, and suspend_until
     * - suspend_until is required
     */
    service_suspend: {
        fields: {
            serviceDates: {
                ...DEFAULT_FIELD,
                visible: true,
                variant: 'suspend',
            },
        },
        sampleContent: SAMPLE_CONTENT['key:service_suspend'],
        validations: {
            service_suspend_until: { required: true },
        },
    },

    /**
     * Meeting
     * - Meeting-specific fields are handled by group template
     * - This override provides specific sample content
     */
    meeting: {
        sampleContent: SAMPLE_CONTENT['key:meeting'],
    },

    /**
     * Internal Problem
     * - Customer and service fields are hidden/optional
     */
    internal_problem: {
        fields: {
            customer: {
                required: false,
                visible: false,
                disabled: false,
            },
            service: {
                required: false,
                visible: false,
                disabled: false,
            },
        },
        sampleContent: SAMPLE_CONTENT['key:internal_problem'],
    },
}

/**
 * Get key-specific override for a ticket type key
 */
export function getKeyOverride(
    key: string
): TicketTemplateOverride | undefined {
    return KEY_OVERRIDES[key]
}
